import { useEffect, useState, lazy, Suspense } from 'react';
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon } from 'lucide-react';
import useCalendarStore from '../../store/calendarStore';
import useNoteStore from '../../store/noteStore';
import CalendarHeader from './CalendarHeader';
import { NoteEditor, NoteList } from '../Note';
import type { CalendarDateResponse } from '../../types/calendar';
import { WEEKDAYS_VI } from '../../types/calendar';

// Lazy load MonthView for better initial load performance
const MonthView = lazy(() => import('./MonthView'));

// Slide animation variants
const slideVariants = {
    enter: (direction: number) => ({
        x: direction > 0 ? 300 : -300,
        opacity: 0,
    }),
    center: {
        x: 0,
        opacity: 1,
    },
    exit: (direction: number) => ({
        x: direction > 0 ? -300 : 300,
        opacity: 0,
    }),
};

// Calendar content that overlays on background - like reference image
const OverlayCalendarContent: React.FC<{ data: CalendarDateResponse }> = ({ data }) => {
    const { solar, lunar, canChi, goodHours, holidays } = data;

    // Check if it's Sunday or has holidays - use red color
    const isSunday = solar.dayOfWeek === 0;
    const hasHoliday = holidays && holidays.length > 0;
    const isSpecialDay = isSunday || hasHoliday;

    // Color classes based on day type - unified to gray-800/90
    const primaryColor = isSpecialDay ? 'text-red-600' : 'text-gray-800/90';
    const secondaryColor = isSpecialDay ? 'text-red-500' : 'text-gray-800/90';
    const tertiaryColor = isSpecialDay ? 'text-red-400' : 'text-gray-800/90';
    const dayNumberColor = isSpecialDay ? 'text-red-600' : 'text-gray-800/90';
    const lunarDayColor = isSpecialDay ? 'text-red-500' : 'text-gray-800/90';
    const hourBgColor = isSpecialDay ? 'text-red-600' : 'text-gray-800/90';

    return (
        <div className={`flex flex-col items-center justify-center h-full px-4 sm:px-6 md:px-8 ${primaryColor}`}>
            {/* Large Day Number - OVERLAYS on background artwork */}
            <div className={`text-[35vw] sm:text-[30vw] md:text-[25vw] lg:text-[20rem] font-bold tracking-tight leading-none drop-shadow-sm ${dayNumberColor}`}>
                {solar.day}
            </div>

            {/* Weekday & Month - below the big number */}
            <div className="flex items-center gap-4 sm:gap-8 mb-4 sm:mb-6 w-full max-w-lg px-[5%]">
                <div className="flex-1 text-left">
                    <div className={`text-xl sm:text-2xl md:text-3xl font-bold uppercase ${primaryColor}`}>
                        {WEEKDAYS_VI[solar.dayOfWeek]}
                    </div>
                    <div className={`text-sm sm:text-base md:text-lg ${secondaryColor}`}>
                        Tháng {solar.month}
                    </div>
                    <div className={`text-xs sm:text-sm ${tertiaryColor}`}>
                        Năm {solar.year}
                    </div>
                </div>

                {/* Lunar Date Number */}
                <div className={`text-6xl sm:text-7xl md:text-8xl font-bold ${lunarDayColor}`}>
                    {lunar.day}
                </div>

                <div className="flex-1 text-right">
                    <div className={`text-xl sm:text-2xl md:text-3xl font-bold ${primaryColor}`}>
                        ÂM LỊCH
                    </div>
                    <div className={`text-sm sm:text-base md:text-lg ${secondaryColor}`}>
                        Tháng {lunar.month}{lunar.isLeapMonth ? ' (Nhuận)' : ''}
                    </div>
                    <div className={`text-xs sm:text-sm ${tertiaryColor}`}>
                        Năm {lunar.yearName}
                    </div>
                </div>
            </div>

            {/* Can Chi & Info */}
            <div className={`text-center mb-3 sm:mb-4 text-sm sm:text-base ${secondaryColor}`}>
                Năm {canChi.year} • Tháng {canChi.month} • Ngày {canChi.day}
            </div>

            {/* Good Hours */}
            {goodHours && goodHours.length > 0 && (
                <div className="text-center mb-3 sm:mb-4">
                    <div className={`text-xs sm:text-sm mb-2 ${tertiaryColor}`}>Giờ hoàng đạo</div>
                    <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                        {goodHours.map((hour, index) => (
                            <span
                                key={index}
                                className={`px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium ${hourBgColor}`}
                            >
                                {hour}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Holidays */}
            {holidays && holidays.length > 0 && (
                <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                    {holidays.map((holiday, index) => (
                        <span
                            key={index}
                            className="px-3 sm:px-4 py-1 bg-red-500 text-white rounded-full text-xs sm:text-sm font-medium"
                        >
                            {holiday}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};


const CalendarView: React.FC = () => {
    const { currentDate, calendarData, isLoading, error, fetchCalendarData, goToPrevDay, goToNextDay, setDate } = useCalendarStore();
    const { openEditor, notes, fetchNotes } = useNoteStore();
    const [headerVisible, setHeaderVisible] = useState(false);
    const [notesVisible, setNotesVisible] = useState(false);
    const [monthViewOpen, setMonthViewOpen] = useState(false);
    const [direction, setDirection] = useState(0);

    // Swipe handling
    const x = useMotionValue(0);
    const opacity = useTransform(x, [-200, 0, 200], [0.5, 1, 0.5]);

    useEffect(() => {
        fetchCalendarData();
    }, [currentDate, fetchCalendarData]);

    // Load notes when date changes
    useEffect(() => {
        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
        fetchNotes(dateStr);
    }, [currentDate, fetchNotes]);

    const handleDragEnd = (_: unknown, info: { offset: { x: number }; velocity: { x: number } }) => {
        const threshold = 50;
        const velocity = info.velocity.x;

        if (info.offset.x > threshold || velocity > 500) {
            setDirection(-1);
            goToPrevDay();
        } else if (info.offset.x < -threshold || velocity < -500) {
            setDirection(1);
            goToNextDay();
        }

        animate(x, 0, { type: 'spring', stiffness: 300, damping: 30 });
    };

    return (
        <div className="h-[100dvh] md:h-screen w-screen overflow-hidden relative max-w-screen-2xl mx-auto pb-16 md:pb-0">
            {/* Full Page Background Image - covers entire screen */}
            <div
                className="absolute inset-0 bg-cover bg-top bg-no-repeat"
                style={{
                    backgroundImage: 'url(/images/HaLong.jpg)',
                }}
            />
            {/* Custom gradient - artwork 0-30%, fade to 90% at bottom */}
            <div className="absolute inset-0"
                style={{ background: 'linear-gradient(to bottom, transparent 0%, transparent 30%, rgba(255,255,255,0.2) 38%, rgba(255,255,255,0.5) 45%, rgba(255,255,255,0.85) 75%, rgba(255,255,255,0.9) 85%, rgba(255,255,255,0.9) 100%)' }}
            />

            <AnimatePresence>
                <CalendarHeader
                    isVisible={headerVisible}
                    onClose={() => setHeaderVisible(false)}
                />
            </AnimatePresence>

            {/* Top right controls - Xem tháng only (desktop) */}
            <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
                {/* Month View Button - Desktop only */}
                <button
                    onClick={() => setMonthViewOpen(true)}
                    aria-label="Xem lịch tháng"
                    className="
                        hidden md:flex
                        items-center gap-2
                        px-3 py-2
                        bg-white/70 hover:bg-white/90
                        border border-gray-300
                        rounded-full
                        shadow-md hover:shadow-lg
                        transition-all
                        text-gray-700 font-medium text-sm
                    "
                >
                    <CalendarIcon className="w-4 h-4" aria-hidden="true" />
                    <span>Xem tháng</span>
                </button>
            </div>

            {/* Main swipeable content - OVERLAYS on background */}
            <motion.div
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={handleDragEnd}
                style={{ x, opacity }}
                className="h-full w-full cursor-grab active:cursor-grabbing touch-none relative z-10"
            >
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-gray-700" />
                    </div>
                )}

                {error && (
                    <div className="absolute inset-0 flex items-center justify-center text-red-600">
                        <div className="text-center bg-white/80 p-4 rounded-lg">
                            <p className="mb-2">{error}</p>
                            <button
                                onClick={fetchCalendarData}
                                className="px-4 py-2 bg-gray-200 rounded-full text-sm hover:bg-gray-300 transition-colors text-gray-700"
                            >
                                Thử lại
                            </button>
                        </div>
                    </div>
                )}

                {!isLoading && !error && calendarData && (
                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.div
                            key={currentDate.toISOString()}
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            className="h-full"
                        >
                            <OverlayCalendarContent data={calendarData} />
                        </motion.div>
                    </AnimatePresence>
                )}
            </motion.div>

            {/* Notes Area - slides up when visible */}
            <AnimatePresence>
                {notesVisible && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: '40%' }}
                        exit={{ height: 0 }}
                        transition={{ type: 'spring', damping: 25 }}
                        className="absolute bottom-0 left-0 right-0 bg-slate-900 overflow-y-auto z-20"
                    >
                        <div className="flex items-center justify-between px-4 sm:px-6 py-2 sm:py-3 border-b border-white/10">
                            <h3 className="text-white font-medium text-base sm:text-lg">Ghi chú ({notes.length})</h3>
                            <div className="flex items-center gap-2">
                                {/* Add new note button */}
                                <button
                                    onClick={() => {
                                        setNotesVisible(false);
                                        openEditor();
                                    }}
                                    className="p-2 bg-blue-500 hover:bg-blue-600 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation active:scale-95 transition-colors"
                                >
                                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                </button>
                                {/* Close button */}
                                <button
                                    onClick={() => setNotesVisible(false)}
                                    className="p-2 hover:bg-white/20 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation active:scale-95"
                                >
                                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <NoteList />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Month View Button - Mobile (floating above swipe hint) */}
            <button
                onClick={() => setMonthViewOpen(true)}
                aria-label="Xem lịch tháng"
                className="
                    md:hidden
                    absolute bottom-28 left-1/2 -translate-x-1/2
                    px-4 py-2
                    bg-white/80 hover:bg-white/90
                    border border-gray-300
                    rounded-full
                    shadow-lg
                    transition-all
                    z-20
                    flex items-center gap-2
                    text-gray-700 font-medium text-sm
                "
            >
                <CalendarIcon className="w-4 h-4" aria-hidden="true" />
                <span>Xem tháng</span>
            </button>

            {/* Removed - Desktop button is now in the top right flex container */}

            {/* Swipe hint */}
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-gray-500 text-xs sm:text-sm z-10">
                ← Vuốt để đổi ngày →
            </div>

            {/* Note Editor Modal */}
            <NoteEditor />

            {/* Month View Modal - Lazy loaded */}
            <Suspense fallback={null}>
                <MonthView
                    isOpen={monthViewOpen}
                    onClose={() => setMonthViewOpen(false)}
                    selectedDate={currentDate}
                    onSelectDate={(date) => {
                        setDate(date);
                    }}
                />
            </Suspense>
        </div>
    );
};

export default CalendarView;
